import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AddBlog.css"; // reuse your styles

const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

export default function EditBlogPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [coverImage, setCoverImage] = useState(null); // new file
  const [coverImageUrl, setCoverImageUrl] = useState(""); // existing url
  const [slug, setSlug] = useState("");
  const [slugEditedManually, setSlugEditedManually] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const quillRef = useRef();

  // Fetch blog data
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/blogs/id/${id}`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "خطا در بارگذاری مقاله");
        }

        const data = await res.json();
        setTitle(data.title || "");
        setContent(data.content || "");
        setCoverImageUrl(data.image || "");
        setSlug(data.slug || "");
      } catch (err) {
        console.error("fetchBlog error:", err);
        toast.error(err.message || "خطا در بارگذاری مقاله");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id, token]);

  // Auto-generate slug when title changes if user hasn't typed manually
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (!slugEditedManually) {
      const generatedSlug = newTitle
        .trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      setSlug(generatedSlug);
    }
  };

  const handleSlugChange = (e) => {
    setSlug(e.target.value);
    setSlugEditedManually(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (coverImage) {
        const formData = new FormData();
        formData.append("title", title);
        formData.append("content", content);
        formData.append("slug", slug);
        formData.append("cover_image", coverImage);
        formData.append("_method", "PUT");

        const res = await fetch(`${API_BASE}/api/blogs/${id}`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: formData,
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "خطا در بروزرسانی مقاله (file)");
        }

        await res.json();
      } else {
        const payload = { title, content, slug };

        const res = await fetch(`${API_BASE}/api/blogs/${id}`, {
          method: "PUT",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "خطا در بروزرسانی مقاله (json)");
        }

        await res.json();
      }

      toast.success("مقاله با موفقیت بروزرسانی شد.");
      navigate("/cms/manage-blogs");
    } catch (err) {
      console.error("handleUpdate error:", err);
      setError(err.message || "خطا در بروزرسانی مقاله");
      toast.error(err.message || "خطا در بروزرسانی مقاله");
    } finally {
      setSaving(false);
    }
  };

  const imageHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("image", file);

      try {
        const res = await fetch(`${API_BASE}/api/blogs/upload-image`, {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: formData,
        });

        const data = await res.json();

        if (data.url) {
          const editor = quillRef.current.getEditor();
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, "image", data.url);
        } else {
          toast.error("آپلود تصویر ناموفق بود.");
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error("خطا در آپلود تصویر");
      }
    };
  };

  const modules = {
    toolbar: {
      container: [
        [{ font: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ header: [1, 2, 3, 4, 5, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image", "blockquote", "code-block"],
        [{ color: [] }, { background: [] }],
        [{ direction: "rtl" }],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
  };

  if (loading) return <p>در حال بارگذاری...</p>;

  return (
    <>
      <form onSubmit={handleUpdate} className="blog-editor-form">
        <label>عنوان</label>
        <input value={title} onChange={handleTitleChange} required />

        <label>اسلاگ</label>
        <input
          value={slug}
          onChange={handleSlugChange}
          placeholder="مثال: my-blog-post"
        />

        <label>تصویر کاور</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setCoverImage(e.target.files[0])}
        />
        {coverImageUrl && !coverImage && (
          <div className="cover-preview">
            <img src={coverImageUrl} alt="Cover" />
          </div>
        )}

        <label>محتوا</label>
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={setContent}
          modules={modules}
          theme="snow"
        />

        {error && <div className="error">{error}</div>}

        <button type="submit" disabled={saving}>
          {saving ? "در حال ذخیره..." : "بروزرسانی مقاله"}
        </button>
      </form>

      <ToastContainer />
    </>
  );
}
